import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHeader, CtaBand } from "../components/site/Sections";
import { Reveal } from "../components/site/Reveal";
import { ServiceIcon } from "../components/site/ServiceIcon";
import { Seo } from "../components/site/Seo";
import { SERVICES } from "../data/site";
import { useCases } from "../lib/useCases";
import NotFound from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { cases } = useCases();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <NotFound />;
  const related = cases.filter((c) => (c.services || []).includes(s.form)).slice(0, 2);
  const others = SERVICES.filter((x) => x.slug !== slug);

  return (
    <>
      <Seo path={`/services/${s.slug}`} title={`${s.title} in Canberra`} description={`${s.short} ${s.title} services from The Catalyst, Canberra.`} />
      <PageHeader eyebrow={`Services / ${s.title}`} lines={[s.title + "."]} intro={s.intro}>
        <Reveal delay={0.45} className="mt-10">
          <Link to={`/contact?service=${encodeURIComponent(s.form)}`} className="btn-primary" data-testid="service-detail-cta">Talk to us about {s.title.toLowerCase()} <ArrowUpRight className="h-4 w-4" /></Link>
        </Reveal>
      </PageHeader>
      <section className="wrap grid gap-6 pb-20 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <div className="h-full rounded-[1.75rem] border border-ink/10 bg-white p-8 sm:p-12" data-testid="service-includes">
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">What you get</h2>
            <ul className="mt-8 divide-y divide-ink/10">
              {s.includes.map((item) => (
                <li key={item} className="flex items-center gap-4 py-4 text-ink">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-flame/10 text-flame"><Check className="h-4 w-4" /></span>{item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="grain relative flex h-full flex-col justify-between overflow-hidden rounded-[1.75rem] bg-navy p-8 text-white sm:p-12">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white/15"><ServiceIcon name={s.icon} className="h-6 w-6" /></span>
            <div className="mt-16">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">The outcome</p>
              <p className="mt-4 font-display text-3xl font-bold leading-tight sm:text-4xl">{s.outcome}</p>
            </div>
          </div>
        </Reveal>
      </section>
      {related.length > 0 && (
        <section className="wrap pb-20">
          <h2 className="font-display text-2xl font-bold text-ink">Proof it works</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {related.map((c) => (
              <Link key={c.slug} to={`/work/${c.slug}`} className="group flex items-center gap-5 rounded-2xl border border-ink/10 bg-white p-4 transition-colors hover:border-ink" data-testid={`related-case-${c.slug}`}>
                <img src={c.image} alt="" loading="lazy" className="h-20 w-24 rounded-xl object-cover" />
                <div className="flex-1"><p className="text-xs font-semibold text-flame">{c.industry}</p><p className="font-display text-lg font-semibold text-ink">{c.client}</p></div>
                <ArrowUpRight className="h-5 w-5 text-ink transition-transform group-hover:rotate-45" />
              </Link>
            ))}
          </div>
        </section>
      )}
      <section className="wrap pb-20 sm:pb-28">
        <h2 className="font-display text-2xl font-bold text-ink">Pairs well with</h2>
        <div className="mt-6 flex flex-wrap gap-3">
          {others.map((o) => <Link key={o.slug} to={`/services/${o.slug}`} className="btn-ghost" data-testid={`other-service-${o.slug}`}>{o.title}</Link>)}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
